"use client";

import { useMemo } from "react";
import { useSelector } from "react-redux";

import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import type { RootState } from "@/store/store";
import {
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_FORM_VARIANTS,
} from "../../../constants";

/**
 * Rich Text Editor 6001 — BDU auxiliary page body
 * (bduedu.in `/student-support-services`).
 *
 * Rich Text Editor 1 laid out as v1's two-column content block: the CMS article
 * on the left, the site's lead form pinned right in a 328px rail that drops away
 * below 768px. Pairs with Banner 6000, which carries the hero above it.
 *
 * LEAD FORM — the reference hardcodes its own form markup. This app resolves it
 * from the tenant's configured Lead Drop Form variant in University Settings
 * (same wiring as About 1037 / About 2 / Banner 10), so the rail picks up
 * whichever form the site uses and inherits its course / qualification lists
 * rather than carrying a duplicate set here. `enable_lead_form` only opts this
 * section in; with no form configured in settings the rail renders nothing and
 * the article takes the full width.
 *
 * That settings state is populated by a client-side mount effect, so — like
 * About 1037 — the rail waits for `useIsMobile` to resolve, which keeps SSR and
 * hydration in agreement instead of re-rendering the whole page.
 *
 * Unlike About 1037 there is NO mobile CTA fallback: v1's auxiliary page simply
 * drops the form below 768px and shows nothing in its place.
 *
 * BREAKPOINTS — v1's `md:` is 768px, which is `tab:` here (this app remaps
 * `sm`/`md`/`lg` to 1080/1230/1430, so a literal `md:` would fire ~460px late).
 *
 * PADDING — v1's class list is `p-10 px-5 py-11 pt-4 md:px-11 md:py-9`, which
 * Tailwind's property ordering resolves to 16/20/44/20 on mobile and 36/44 on
 * desktop. Written out resolved here so the cascade cannot drift.
 *
 * The article HTML is injected directly rather than through `CmsHtml` — its
 * `normalizeCmsHtml` strips everything after the first paragraph.
 */

interface RichTextEditor6001Data {
  status?: boolean | number | string;
  content?: string;
  background_color?: string;
  /** Opt this section into the site-wide lead form rail. */
  enable_lead_form?: boolean | number | string;
}

/** MySQL TINYINT(1) → boolean; Sequelize may return 0/1, "0"/"1", or boolean. */
function toBool(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["0", "false", "no", "off", ""].includes(t)) return false;
    if (["1", "true", "yes", "on"].includes(t)) return true;
  }
  return Boolean(value);
}

const RichTextEditor6001 = ({ data }: { data: any }) => {
  const sectionData: RichTextEditor6001Data | undefined = data?.data ?? data;
  const isMobile = useIsMobile(768);

  const leaddropFormVariant = useSelector(
    (state: RootState) => state.leaddropform.variant,
  );
  const leadDropFormRow = useSelector((state: RootState) => {
    const blob = state.leaddropform.lead_drop_form as
      | { formData?: Record<string, unknown> }
      | null
      | undefined;
    return blob?.formData ?? null;
  });

  const LeaddropFormComponent = useMemo(() => {
    if (!leaddropFormVariant) return null;
    return LEAD_DROP_FORM_SECTION_VARIANTS[leaddropFormVariant] ?? null;
  }, [leaddropFormVariant]);

  if (!sectionData || !toBool(sectionData.status, true)) return null;

  const { content = "", background_color = "" } = sectionData;
  const enableLeadForm = toBool(sectionData.enable_lead_form, false);

  /* The rail only takes a column when a form will actually land in it, so an
     unconfigured tenant gets a full-width article instead of a 328px gap. */
  const showRail = enableLeadForm && !isMobile && !!LeaddropFormComponent;

  return (
    <section
      className="w-full"
      style={background_color ? { backgroundColor: background_color } : undefined}
    >
      <div
        className={cn(
          "mx-auto w-full max-w-[1448px] px-5 pb-11 pt-4 tab:px-11 tab:py-9",
          showRail && "tab:grid tab:grid-cols-[1fr_328px] tab:gap-x-14",
        )}
      >
        <div
          className={cn(
            "break-words",
            "[&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul_li]:mt-2",
            "[&_a]:text-[#1570EF] [&_a]:underline [&_a]:hover:text-[#1E40AF]",
          )}
          dangerouslySetInnerHTML={{ __html: content }}
        />

        {/* Desktop — the site's inline lead form, from University Settings. */}
        {showRail && (
          <div className="hidden overflow-hidden tab:block tab:shrink-0">
            {leaddropFormVariant === LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_6 &&
            leadDropFormRow ? (
              <LeaddropFormComponent data={{ data: leadDropFormRow }} />
            ) : (
              <LeaddropFormComponent />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default RichTextEditor6001;
